import { Handle, NodeProps, Position, useReactFlow } from '@xyflow/react'
import { ProcessFlowDiagramBuilderContext } from 'features/processFlowDiagrams/contexts/ProcessFlowDiagramBuilderContext'
import { resolveHighlightColor } from 'features/processFlowDiagrams/utils/helpers'
import { useRolodexSignedURLs } from 'features/rolodex'
import React, { useContext, useMemo } from 'react'
import styled from 'styled-components'
import { PfdNode } from 'types'

import { BaseNodeWrapper } from './BaseNodeWrapper'

const Container = styled.div<{
  width: number
  height: number
  $rotation: number
  $isSelected: boolean
  $highlightColor?: string | null
  $scale?: number
}>`
  position: relative;
  width: ${(p) => p.width}px;
  height: ${(p) => p.height}px;
  display: inline-block;
  color: ${({ theme }) => theme.forecast.activeText};
  transform: rotate(${({ $rotation }) => $rotation}deg)
    scale(
      ${({ $scale }) => ($scale === null || $scale === undefined ? 1 : $scale)}
    );

  ${({ $isSelected, $highlightColor }) =>
    $isSelected &&
    $highlightColor &&
    `
    filter: drop-shadow(0 0 6px ${$highlightColor}) drop-shadow(0 0 12px rgba(255, 255, 255, 0.4));

    svg, img {
      transform: scale(1.06);
      transform-origin: center;
      transition: transform 150ms ease-in-out;
    }
  `}
`

const FillContainer = styled.div`
  width: 100%;
  height: 100%;
  color: currentColor;

  svg {
    width: 100%;
    height: 100%;
    display: block;
  }
`

const FillImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: contain;
`

const PortWrapper = styled.div`
  position: absolute;
  transform: translate(-50%, -50%);
  pointer-events: auto;
`

const CenteredHandle = styled(Handle)`
  left: 0;
  top: 0;
`

const PreviewImage = styled.img`
  width: 2rem;
  height: 2rem;
  object-fit: contain;
`

const PreviewBox = styled.div`
  width: 2rem;
  height: 2rem;
  overflow: hidden;
  color: currentColor;
`

export type CustomSymbolTemplate = {
  // kind defaults to 'svg' for backwards compatibility
  kind?: 'svg' | 'image'
  // SVG variant
  svg?: string
  viewBox?: { width: number; height: number }
  // Image variant
  imageDataUrl?: string // preferred for inline previews/prototyping
  image?: { entry_id: string; path: string; content_type: string } // persisted file ref
  size?: { width: number; height: number }
  // Normalized ports (0..1 relative to width/height)
  ports: Array<{
    id: string
    x: number
    y: number
  }>
}

export const CustomSymbolNode: React.FC<
  NodeProps<PfdNode> & { template: CustomSymbolTemplate }
> = ({ id, data, template }) => {
  const { setNodes } = useReactFlow<PfdNode>()
  const { selectedNode, siteID } = useContext(ProcessFlowDiagramBuilderContext)

  const isImage = (template.kind ?? 'svg') === 'image'
  const width = isImage
    ? (template.size?.width ?? template.viewBox?.width ?? 100)
    : (template.viewBox?.width ?? 100)
  const height = isImage
    ? (template.size?.height ?? template.viewBox?.height ?? 100)
    : (template.viewBox?.height ?? 100)
  const { url: signedUrl } = useRolodexSignedURLs(
    isImage && !template.imageDataUrl && template.image?.path
      ? { entry_id: template.image.entry_id, path: template.image.path }
      : undefined
  )
  const imgSrc = template.imageDataUrl || signedUrl || template.image?.path

  const isSelected = useMemo(() => {
    if (!selectedNode) return false
    return selectedNode.id === id
  }, [selectedNode, id])

  const selectedHighlight = useMemo(() => {
    if (!isSelected) return undefined
    const status = data?.status as unknown as string | undefined
    return resolveHighlightColor(status)
  }, [isSelected, data])

  return (
    <BaseNodeWrapper
      id={id}
      link={data.link}
      siteId={siteID}
      setNodes={setNodes}>
      <Container
        width={width}
        height={height}
        $rotation={data.rotation ?? 0}
        $isSelected={isSelected}
        $highlightColor={selectedHighlight}
        $scale={data.scale ?? 1}>
        {isImage ? (
          <FillImage alt={data.label || 'Custom node image'} src={imgSrc} />
        ) : (
          <FillContainer
            dangerouslySetInnerHTML={{
              __html: (template.svg || '')
                .replace(/color:\s*#ffffff/gi, 'color: currentColor')
                .replace(/color:\s*#fff\b/gi, 'color: currentColor'),
            }}
          />
        )}
        {template.ports.map((p) => (
          <PortWrapper
            key={p.id}
            style={{ left: `${p.x * 100}%`, top: `${p.y * 100}%` }}>
            <CenteredHandle
              id={`${p.id}-s`}
              type='source'
              position={Position.Top}
            />
            <CenteredHandle
              id={`${p.id}-t`}
              type='source'
              position={Position.Top}
            />
          </PortWrapper>
        ))}
      </Container>
    </BaseNodeWrapper>
  )
}

export const CustomSymbolPreview: React.FC<{
  template: CustomSymbolTemplate
}> = ({ template }) => {
  const isImage = (template.kind ?? 'svg') === 'image'
  if (isImage) {
    const imgSrc = template.imageDataUrl || template.image?.path
    return <PreviewImage alt='Custom node preview' src={imgSrc} />
  }
  return <PreviewBox dangerouslySetInnerHTML={{ __html: template.svg || '' }} />
}
